<script lang="ts">
	import { createEventDispatcher } from 'svelte';
	import { Button, Modal, Stack, Text, Textarea } from '@Shelly/shelly-design-system';
	import { devices } from '../../../stores';
	import { decodeFlowToken } from '../sharing/flowToken';
	import type { FlowImportMapping, PortableFlowPackage } from '../sharing/types';

	export let showModal = false;
	export let isMobile = false;
	export let mode: 'export' | 'import' = 'export';
	export let buildExport:
		| ((includeNotes: boolean) => { token: string; flow: PortableFlowPackage })
		| undefined = undefined;

	const dispatch = createEventDispatcher<{
		close: void;
		import: { flow: PortableFlowPackage; mapping: FlowImportMapping };
	}>();

	let token = '';
	let parsedFlow: PortableFlowPackage | null = null;
	let includeNotes = true;
	let mappings: FlowImportMapping = {};
	let error = '';
	let copied = false;
	let initializedKey = '';

	$: modalKey = `${showModal}:${mode}`;
	$: if (showModal && initializedKey !== modalKey) {
		initializedKey = modalKey;
		reset();
	}

	$: deviceOptions = Object.values($devices || {}).map((device: any) => ({
		id: String(device.id),
		label: String(device.name || device.id),
	}));

	function reset() {
		error = '';
		copied = false;
		parsedFlow = null;
		mappings = {};
		if (mode === 'export') refreshExport();
		else token = '';
	}

	function refreshExport() {
		if (!buildExport) return;
		try {
			const result = buildExport(includeNotes);
			token = result.token;
			parsedFlow = result.flow;
			error = '';
		} catch (reason) {
			error = reason instanceof Error ? reason.message : String(reason);
		}
	}

	async function copyToken() {
		try {
			await navigator.clipboard.writeText(token);
			copied = true;
		} catch {
			const area = document.querySelector<HTMLTextAreaElement>('#flow-sharing-token textarea');
			area?.select();
			document.execCommand('copy');
			copied = true;
		}
	}

	function parseToken() {
		try {
			parsedFlow = decodeFlowToken(token);
			mappings = {};
			for (const requirement of parsedFlow.requirements) {
				if (requirement.kind !== 'device') continue;
				const match = deviceOptions.find(
					(device) => device.label.toLowerCase() === requirement.label.toLowerCase(),
				);
				if (match) mappings = { ...mappings, [requirement.id]: match.id };
			}
			error = '';
		} catch (reason) {
			parsedFlow = null;
			error = reason instanceof Error ? reason.message : String(reason);
		}
	}

	function updateMapping(requirementId: string, value: string) {
		mappings = { ...mappings, [requirementId]: value };
	}

	function requirementLabel(requirement: PortableFlowPackage['requirements'][number]): string {
		if (!requirement.label.startsWith('Required ')) return requirement.label;
		const nodeLabels =
			parsedFlow?.nodes
				.filter((node) => requirement.usedByNodeIds.includes(node.id))
				.map((node) => String(node.data.displayLabel || node.data.label || '').trim())
				.filter(Boolean) || [];
		const uniqueLabels = [...new Set(nodeLabels)];
		return uniqueLabels.length
			? `${requirement.kind[0].toUpperCase()}${requirement.kind.slice(1)} for ${uniqueLabels.join(', ')}`
			: requirement.label;
	}

	function importFlow() {
		if (!parsedFlow) return;
		dispatch('import', { flow: parsedFlow, mapping: mappings });
	}
</script>

<Modal
	{showModal}
	title={mode === 'export' ? 'Export Flow token' : 'Import Flow token'}
	type={isMobile ? 'fullScreen' : 'default'}
	backdropClose={false}
	on:close={() => dispatch('close')}
>
	<svelte:fragment slot="content">
		<Stack gap="m">
			<Text variant="body" size="m">
				{mode === 'export'
					? 'Copy this token and send it directly to another Shelly Flow user.'
					: 'Paste a Flow token. Imported resources must be mapped to devices in this account.'}
			</Text>
			<div class="security-note">
				<i class="fas fa-triangle-exclamation" aria-hidden="true"></i>
				<span
					>This token is encoded, not signed or encrypted. Treat imported content as untrusted
					and review it before saving.</span
				>
			</div>

			{#if mode === 'export'}
				<label class="include-notes">
					<input type="checkbox" bind:checked={includeNotes} on:change={refreshExport} />
					Include Sticky Notes
				</label>
			{/if}

			<Textarea
				id="flow-sharing-token"
				label="Flow token"
				value={token}
				resize="auto"
				class="token-area"
				on:input={(event: any) => {
					token = String(event?.detail?.target?.value ?? event?.target?.value ?? token);
					if (mode === 'import') parsedFlow = null;
				}}
			/>

			{#if error}
				<div class="error-message" role="alert">{error}</div>
			{/if}

			{#if parsedFlow}
				<div class="manifest">
					<strong>{parsedFlow.metadata.name || 'Imported Flow'}</strong>
					<span>{parsedFlow.nodes.length} nodes · {parsedFlow.edges.length} connections</span>
					{#each parsedFlow.warnings as warning}
						<span class="warning">{warning}</span>
					{/each}
				</div>
			{/if}

			{#if mode === 'import' && parsedFlow?.requirements.length}
				<div class="mapping-list">
					<strong>Required resources</strong>
					<span class="mapping-hint">
						Unmapped resources are left blank so you can configure those nodes later.
					</span>
					{#each parsedFlow.requirements as requirement (requirement.id)}
						<label>
							<span>{requirementLabel(requirement)}</span>
							{#if requirement.kind === 'device'}
								<select
									value={mappings[requirement.id] || ''}
									on:change={(event) =>
										updateMapping(requirement.id, event.currentTarget.value)}
								>
									<option value="">Leave unresolved</option>
									{#each deviceOptions as device (device.id)}
										<option value={device.id}>{device.label}</option>
									{/each}
								</select>
							{:else}
								<input
									value={mappings[requirement.id] || ''}
									placeholder="Local {requirement.kind} ID or leave unresolved"
									on:input={(event) =>
										updateMapping(requirement.id, event.currentTarget.value)}
								/>
							{/if}
						</label>
					{/each}
				</div>
			{/if}

			<div class="actions">
				<Button variant="secondary" on:click={() => dispatch('close')}>Cancel</Button>
				{#if mode === 'export'}
					<Button variant="primary" icon="fas fa-copy" disabled={!token} on:click={copyToken}>
						{copied ? 'Copied' : 'Copy token'}
					</Button>
				{:else if parsedFlow}
					<Button variant="primary" icon="fas fa-file-import" on:click={importFlow}>
						Create Flow copy
					</Button>
				{:else}
					<Button
						variant="primary"
						icon="fas fa-shield-check"
						disabled={!token}
						on:click={parseToken}
					>
						Review token
					</Button>
				{/if}
			</div>
		</Stack>
	</svelte:fragment>
</Modal>

<style>
	.security-note,
	.error-message,
	.manifest,
	.mapping-list {
		padding: 12px;
		border: 1px solid var(--color-3, rgba(148, 163, 184, 0.3));
		border-radius: 8px;
	}
	.security-note {
		display: flex;
		gap: 8px;
		color: var(--color-warning, #b45309);
	}
	.include-notes {
		display: flex;
		align-items: center;
		gap: 8px;
	}
	:global(.token-area textarea) {
		min-height: 150px;
		font-family: monospace;
		font-size: 11px;
	}
	.error-message {
		color: var(--color-danger, #dc2626);
	}
	.manifest,
	.mapping-list,
	.mapping-list label {
		display: flex;
		flex-direction: column;
		gap: 8px;
	}
	.manifest span {
		color: var(--color-text-secondary, #64748b);
	}
	.manifest .warning {
		color: var(--color-warning, #b45309);
	}
	.mapping-hint {
		color: var(--color-text-secondary, #64748b);
		font-size: 12px;
	}
	.mapping-list select,
	.mapping-list input {
		width: 100%;
		min-height: 36px;
		padding: 6px 8px;
		border: 1px solid var(--color-3, rgba(148, 163, 184, 0.3));
		border-radius: 6px;
		background: var(--color-bg, transparent);
		color: inherit;
	}
	.actions {
		display: flex;
		justify-content: flex-end;
		gap: 8px;
	}
	@media (max-width: 639px) {
		.actions {
			align-items: stretch;
			flex-direction: column-reverse;
		}
		.actions > :global(*) {
			width: 100%;
		}
	}
</style>
